import {
  getGlobalSearchPatientsDialog,
  openGlobalSearchPatientsDialog,
} from '@/modules/patients/components/dialogs/global-search-patients-dialog/util'
import {openShortcutsCheatsheetDialog} from '@/modules/shortcuts/components/shortcuts-cheatsheet-dialog/util'
import {hasConfigurationAccess} from '@/modules/auth/permissions'
import {SHORTCUTS} from '@/modules/shortcuts/constants'
import {focusNextRow, focusPrevRow, getRows} from '@/modules/shortcuts/functions'
import {usePermissions} from '@/shared/hooks/use-permissions'
import {useHotkeys} from '@tanstack/react-hotkeys'
import {useNavigate, useRouter} from '@tanstack/react-router'
import {useCallback} from 'react'

export function useShortcuts() {
  const navigate = useNavigate()
  const router = useRouter()
  const {permissions} = usePermissions()
  const canOpenConfiguration = hasConfigurationAccess(permissions)

  useTableKeyboardNav()

  useHotkeys([
    {
      hotkey: SHORTCUTS.SEARCH_PATIENTS,
      callback: () => {
        const dialog = getGlobalSearchPatientsDialog()

        if (!dialog?.id) return openGlobalSearchPatientsDialog()

        const element = document.getElementById(`dialog:${dialog.id}:content`)
        const input = element?.querySelector('input[type="search"]') as HTMLInputElement | null
        input?.focus?.()
      },
    },
    {
      hotkey: SHORTCUTS.PATIENTS,
      callback: () => navigate({to: '/patient-dashboard/patients'}),
    },
    {
      hotkey: SHORTCUTS.ACTIVITY,
      callback: () => navigate({to: '/activity'}),
    },
    {
      hotkey: SHORTCUTS.PRODUCTION,
      callback: () => {
        if (!permissions.has('production.access')) return
        navigate({to: '/production'})
      },
    },
    {
      hotkey: SHORTCUTS.CONFIGURATION,
      callback: () => {
        if (!canOpenConfiguration) return
        navigate({to: '/configuration'})
      },
    },
    {hotkey: SHORTCUTS.BACK, callback: () => router.history.back()},
    {
      hotkey: SHORTCUTS.CHEAT_SHEET,
      callback: openShortcutsCheatsheetDialog,
    },
  ])
}

function useTableKeyboardNav() {
  const navigate = useNavigate()

  const canMoveFocus = useCallback(() => {
    const active = document.activeElement
    return active === document.body || active?.getAttribute('data-role') === 'table-row'
  }, [])

  const getTableRowUrl = useCallback((): string | null => {
    const active = document.activeElement as HTMLElement
    if (active?.getAttribute('data-role') !== 'table-row') return null
    const url = active.getAttribute('data-url')
    return url
  }, [])

  useHotkeys([
    {
      hotkey: SHORTCUTS.NAVIGATE_ROW_DOWN,
      callback: e => {
        e.preventDefault()
        const rows = getRows('table-row')
        if (!rows.length || !canMoveFocus()) return
        focusNextRow(rows)
      },
      options: {ignoreInputs: true, conflictBehavior: 'allow'},
    },
    {
      hotkey: SHORTCUTS.NAVIGATE_ROW_UP,
      callback: e => {
        e.preventDefault()
        const rows = getRows('table-row')
        if (!rows.length || !canMoveFocus()) return
        focusPrevRow(rows)
      },
      options: {ignoreInputs: true, conflictBehavior: 'allow'},
    },
    {
      hotkey: 'Escape',
      callback: () => {
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur()
        }
      },
      options: {ignoreInputs: false},
    },
    {
      hotkey: SHORTCUTS.VIEW_ROW_PAGE,
      callback: e => {
        e.preventDefault()
        const url = getTableRowUrl()
        if (url) {
          navigate({to: url})
        }
      },
    },
    {
      hotkey: SHORTCUTS.VIEW_ROW_PAGE_NEW_TAB,
      callback: e => {
        e.preventDefault()
        const url = getTableRowUrl()
        if (url) {
          window.open(url, '_blank')
        }
      },
    },
  ])
}
